import { useEffect, useState } from "react";
import {
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Mail,
  Tag,
  ShieldCheck,
  ShoppingBag,
  Facebook,
  Instagram,
  Linkedin,
  Scissors,
  Smile,
  Baby,
  User,
  Crown,
  Clock,
  CreditCard,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const PHILIPS_BLUE = "#0081c4";
const ISIC_TEAL = "#40b8b8";

const SIGNUP_URL = "https://www.philips.ro/c-e/member-signup-genz.html";
const ISIC_VOUCHERS_URL = "https://www.isic.ro/reduceri/ro/philips/9807";
const ISIC_APPLY_URL = "https://aplica.isic.ro/?issuer_id=56407";

const PHILIPS_LOGO =
  "https://customer-assets.emergentagent.com/job_isic-preview/artifacts/rghd8dh2_Philips_logo_new.svg.png";
const ISIC_LOGO =
  "https://customer-assets.emergentagent.com/job_isic-preview/artifacts/el433v3n_ISIC_logo_COLOUR_rgb.png";

const Landing = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className="min-h-screen text-neutral-900"
      style={{
        fontFamily:
          "'Manrope', 'Work Sans', system-ui, -apple-system, Segoe UI, sans-serif",
        background: "#fafaf7",
      }}
      data-testid="landing-page"
    >
      {/* HEADER */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/90 backdrop-blur-xl border-b border-neutral-200/70 py-3"
            : "bg-transparent py-5"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between">
          <img
            src={ISIC_LOGO}
            alt="ISIC"
            className="h-7 md:h-8 w-auto object-contain"
            data-testid="header-logo-isic"
          />
          <img
            src={PHILIPS_LOGO}
            alt="Philips"
            className="h-5 md:h-6 w-auto object-contain"
            data-testid="header-logo-philips"
          />
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative pt-40 md:pt-48 pb-20 md:pb-28 overflow-hidden"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 dots-pattern opacity-70 pointer-events-none" />
        <div
          className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-30 pointer-events-none float-anim"
          style={{
            background:
              "radial-gradient(circle, rgba(0,129,196,0.6) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute -bottom-40 -left-32 w-[480px] h-[480px] rounded-full blur-3xl opacity-30 pointer-events-none float-anim"
          style={{
            background:
              "radial-gradient(circle, rgba(64,184,184,0.6) 0%, transparent 70%)",
            animationDelay: "2s",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <div className="w-[92%] max-w-6xl mx-auto mb-14 fade-up">
            <div
              className="absolute -inset-6 rounded-[2.5rem] blur-3xl opacity-30 pointer-events-none"
              style={{
                background:
                  "linear-gradient(135deg, rgba(0,129,196,0.5), rgba(64,184,184,0.4))",
              }}
            />
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl bg-white border border-white/50">
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_isic-preview/artifacts/f64zap9g_2000x1000.jpg"
                alt="ISIC × Philips — 25% extra reducere la prima comandă"
                className="w-full h-auto block"
                data-testid="hero-banner"
              />
            </div>
          </div>

          <div className="max-w-3xl mx-auto text-center fade-up" style={{ animationDelay: "0.15s" }}>
            <p
              className="text-lg md:text-2xl text-neutral-700 leading-relaxed mb-10"
              data-testid="hero-description"
            >
              Ești posesor de legitimație{" "}
              <strong className="text-neutral-900">ISIC / BRD-ISIC, ITIC sau IYTC</strong>?
              Beneficiezi de o extra-reducere de bun venit exclusivă de{" "}
              <span className="font-bold" style={{ color: PHILIPS_BLUE }}>
                25%
              </span>{" "}
              pe Philips.ro la toate produsele de îngrijire personală.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-primary"
              >
                <Button
                  className="btn-philips rounded-full h-14 px-8 text-base shadow-none pulse-ring"
                  style={{ background: PHILIPS_BLUE }}
                >
                  Obține reducerea de 25%
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a
                href={ISIC_VOUCHERS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-secondary"
              >
                <Button
                  variant="outline"
                  className="rounded-full h-14 px-8 text-base border-2 hover:bg-neutral-50"
                  style={{
                    borderColor: ISIC_TEAL,
                    color: ISIC_TEAL,
                  }}
                >
                  Voucher 15% lunar
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* OFFER SECTION — single column, no banner */}
      <section id="oferta" className="relative py-10" data-testid="offer-section">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div
            className="rounded-3xl overflow-hidden border border-neutral-200 shadow-sm p-8 md:p-14"
            style={{
              background:
                "linear-gradient(105deg, #ffffff 0%, #ffffff 55%, rgba(64,184,184,0.06) 100%)",
            }}
          >
            <p
              className="text-xs font-bold uppercase tracking-[0.2em] mb-3"
              style={{ color: ISIC_TEAL }}
            >
              Oferta de bun venit
            </p>
            <h2
              className="text-3xl md:text-4xl font-extrabold tracking-tight mb-5"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              data-testid="offer-title"
            >
              <span style={{ color: PHILIPS_BLUE }}>25% extra reducere</span>{" "}
              la prima comandă
            </h2>
            <p
              className="text-neutral-700 leading-relaxed mb-5 max-w-3xl"
              data-testid="offer-description"
            >
              Reducerea se acordă sub forma unui cod de discount primit pe
              mail, după completarea formularului de abonare în Comunitatea
              Philips. Valabil pentru comenzi de minimum 300 lei.
            </p>
            <p
              className="text-sm md:text-base text-neutral-600 mb-7"
              data-testid="offer-eligibility"
            >
              Se aplică posesorilor{" "}
              <strong className="text-neutral-900">ISIC</strong>,{" "}
              <strong className="text-neutral-900">BRD-ISIC</strong>,{" "}
              <strong className="text-neutral-900">ITIC</strong> și{" "}
              <strong className="text-neutral-900">IYTC</strong>.
            </p>
            <a
              href={ISIC_APPLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold group"
              style={{ color: ISIC_TEAL }}
              data-testid="apply-isic-link"
            >
              Nu ai legitimație? Aplică în 3 minute
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        id="cum-functioneaza"
        className="py-20 md:py-28"
        data-testid="how-it-works-section"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <p
              className="text-xs font-bold uppercase tracking-[0.2em] mb-3"
              style={{ color: PHILIPS_BLUE }}
            >
              4 pași simpli
            </p>
            <h2
              className="text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.05] mb-4"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              data-testid="how-it-works-title"
            >
              Cum obții 25% extra reducere la prima comandă
            </h2>
            <p className="text-base text-neutral-600">
              Tot procesul durează mai puțin de 10 minute. Codul ajunge direct
              pe e-mail.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                n: "01",
                icon: <CreditCard className="w-5 h-5" />,
                title: "Ai legitimație ISIC valabilă",
                body: (
                  <>
                    Trebuie să fii posesor de legitimație ISIC / BRD-ISIC, ITIC
                    sau IYTC. Dacă nu ai una,{" "}
                    <a
                      href={ISIC_APPLY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold"
                      style={{ color: ISIC_TEAL }}
                      data-testid="step-1-apply-link"
                    >
                      aplică AICI
                    </a>{" "}
                    în doar 3 minute.
                  </>
                ),
              },
              {
                n: "02",
                icon: <User className="w-5 h-5" />,
                title: "Te abonezi la Comunitatea Philips",
                body: (
                  <>
                    Intri{" "}
                    <a
                      href={SIGNUP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold"
                      style={{ color: PHILIPS_BLUE }}
                      data-testid="step-2-signup-link"
                    >
                      AICI
                    </a>{" "}
                    și completezi formularul de abonare în Comunitatea Philips.
                  </>
                ),
              },
              {
                n: "03",
                icon: <Mail className="w-5 h-5" />,
                title: "Primești codul pe e-mail",
                body: (
                  <>
                    În maxim 10 minute primești pe adresa înscrisă codul tău
                    exclusiv de reducere pentru magazinul oficial Philips.ro.
                  </>
                ),
              },
              {
                n: "04",
                icon: <ShoppingBag className="w-5 h-5" />,
                title: "Te bucuri de 25% reducere",
                body: (
                  <>
                    Valabil pentru achiziția de produse Philips în valoare de
                    minim 300 lei.
                  </>
                ),
              },
            ].map((step, i) => (
              <Card
                key={i}
                className="step-card relative p-6 rounded-2xl border border-neutral-200/80 bg-white shadow-none"
                data-testid={`step-card-${i + 1}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{
                      background: "rgba(0,129,196,0.08)",
                      color: PHILIPS_BLUE,
                    }}
                  >
                    {step.icon}
                  </div>
                  <span
                    className="text-xs font-bold tracking-widest"
                    style={{ color: "rgba(0,0,0,0.25)" }}
                  >
                    {step.n}
                  </span>
                </div>
                <h3
                  className="text-lg font-bold mb-2.5 leading-snug"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {step.title}
                </h3>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  {step.body}
                </p>
              </Card>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <a
              href={SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="how-it-works-cta"
            >
              <Button
                className="btn-philips rounded-full h-14 px-10 text-base shadow-none"
                style={{ background: PHILIPS_BLUE }}
              >
                Obține reducerea de 25%
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* 15% RECURRING — minimal, title + button only */}
      <section
        className="py-20 md:py-24 relative"
        data-testid="recurring-section"
      >
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div
            className="relative rounded-[2rem] overflow-hidden p-10 md:p-16 text-center"
            style={{
              background:
                "linear-gradient(120deg, #073b5c 0%, #0081c4 55%, #40b8b8 100%)",
            }}
          >
            <div className="absolute inset-0 dots-pattern opacity-10 pointer-events-none" />
            <div
              className="absolute -right-24 -bottom-24 w-[480px] h-[480px] rounded-full blur-3xl opacity-40 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)",
              }}
            />

            <div className="relative max-w-3xl mx-auto">
              <h2
                className="text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.05] mb-9 text-white"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="recurring-title"
              >
                Ai folosit deja 25%? Continuă cu{" "}
                <span className="underline decoration-white/50 underline-offset-8">
                  15% lunar
                </span>
              </h2>

              <a
                href={ISIC_VOUCHERS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="recurring-cta-button"
              >
                <Button
                  className="rounded-full h-14 px-10 text-base font-semibold shadow-none"
                  style={{ background: "#ffffff", color: PHILIPS_BLUE }}
                >
                  Obține reducerea de 15%
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section
        id="produse"
        className="py-20 md:py-28"
        data-testid="products-section"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
            <div className="lg:col-span-7">
              <p
                className="text-xs font-bold uppercase tracking-[0.2em] mb-3"
                style={{ color: PHILIPS_BLUE }}
              >
                Categorii participante
              </p>
              <h2
                className="text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.05]"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="products-title"
              >
                La ce produse se aplică reducerea
              </h2>
            </div>
            <div className="lg:col-span-5 text-neutral-600">
              Produsele participante în campanie, la care se aplică reducerea de
              25% extra și voucherele lunare de 15%.
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: <Scissors className="w-6 h-6" />,
                title: "Îngrijire pentru femei",
                body: "Uscătoare, plăci de păr, epilatoare electrice și IPL, aparate de coafat.",
                tag: "Hair & Beauty",
              },
              {
                icon: <User className="w-6 h-6" />,
                title: "Îngrijire pentru bărbați",
                body: "Aparate de tuns barbă, față și corp.",
                tag: "Grooming",
              },
              {
                icon: <Smile className="w-6 h-6" />,
                title: "Îngrijire orală",
                body: "Gama completă Philips Sonicare pentru o curățare profesională.",
                tag: "Sonicare",
              },
              {
                icon: <Baby className="w-6 h-6" />,
                title: "Mama și copilul",
                body: "Gama Philips AVENT pentru îngrijirea celor mici.",
                tag: "AVENT",
              },
            ].map((p, i) => (
              <Card
                key={i}
                className="product-card group p-6 rounded-2xl border border-neutral-200/80 bg-white shadow-none overflow-hidden relative"
                data-testid={`product-card-${i + 1}`}
              >
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(0,129,196,0.04), rgba(64,184,184,0.04))",
                  }}
                />
                <div className="relative">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                    style={{
                      background: "rgba(64,184,184,0.1)",
                      color: ISIC_TEAL,
                    }}
                  >
                    {p.icon}
                  </div>
                  <h3
                    className="text-lg font-bold mb-2 leading-tight"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {p.title}
                  </h3>
                  <p className="text-sm text-neutral-600 leading-relaxed mb-5">
                    {p.body}
                  </p>
                  <span className="inline-block text-xs font-medium px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-700">
                    {p.tag}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* DETAILS */}
      <section
        id="detalii"
        className="py-20 md:py-24 bg-white border-y border-neutral-200/80"
        data-testid="details-section"
      >
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <p
                className="text-xs font-bold uppercase tracking-[0.2em] mb-3"
                style={{ color: ISIC_TEAL }}
              >
                Termeni & condiții
              </p>
              <h2
                className="text-2xl md:text-3xl font-extrabold tracking-tight leading-[1.1]"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="details-title"
              >
                Detalii privind ofertele și reducerile disponibile
              </h2>
            </div>
            <div className="md:col-span-8 space-y-6 text-neutral-700 leading-relaxed">
              <p>
                La abonarea în Comunitatea Philips prin intermediul paginii de
                campanie ISIC, membrii vor primi 25% extra reducere pentru
                achiziția produselor de îngrijire personală de pe Philips.ro.
                Reducerea se acordă sub forma unui cod de discount serial,
                primit pe e-mail după completarea formularului, valabil doar
                pentru comenzile de minimum 300 lei. Oferta de 25% extra nu se
                cumulează cu alte coduri de reducere și se aplică T&amp;C.
              </p>
              <p>
                Pe lângă oferta exclusivă de bun venit de 25% extra reducere,
                poți accesa oricând o ofertă de 15% extra reducere direct din
                platforma ISIC, în limita a 3 vouchere pe lună.
              </p>

              <Separator />

              <div>
                <p className="font-bold text-neutral-900 mb-3">
                  Produsele participante în campanie:
                </p>
                <ul className="space-y-2.5">
                  {[
                    "Produse de îngrijire pentru femei: uscătoare, plăci de păr, epilatoare electrice și IPL, aparate de coafat",
                    "Produse de îngrijire pentru bărbați: aparate de tuns barbă, față și corp",
                    "Produse de îngrijire orală, gama Philips Sonicare",
                    "Produse pentru îngrijirea mamei și copilului, gama Philips AVENT",
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3 items-start">
                      <CheckCircle2
                        className="w-5 h-5 mt-0.5 shrink-0"
                        style={{ color: PHILIPS_BLUE }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-24" data-testid="final-cta-section">
        <div className="max-w-5xl mx-auto px-5 md:px-10 text-center">
          <h2
            className="text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.05] mb-6"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            data-testid="final-cta-title"
          >
            Gata să economisești pe{" "}
            <span style={{ color: PHILIPS_BLUE }}>Philips.ro</span>?
          </h2>
          <p className="text-base md:text-lg text-neutral-600 max-w-2xl mx-auto mb-10">
            Activează acum codul tău exclusiv de 25% sau accesează voucherele
            lunare de 15%. Beneficii reale, disponibile imediat.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-25"
            >
              <Button
                className="btn-philips rounded-full h-14 px-10 text-base shadow-none"
                style={{ background: PHILIPS_BLUE }}
              >
                Obține reducerea de 25%
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <a
              href={ISIC_VOUCHERS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-15"
            >
              <Button
                className="rounded-full h-14 px-10 text-base shadow-none text-white"
                style={{ background: ISIC_TEAL }}
              >
                Obține reducerea de 15%
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        className="border-t border-neutral-200/80 py-12"
        data-testid="site-footer"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span
                className="text-lg font-extrabold tracking-tight"
                style={{ color: ISIC_TEAL }}
                data-testid="footer-wordmark-isic"
              >
                ISIC
              </span>
              <span className="text-neutral-400 font-light">×</span>
              <span
                className="text-lg font-extrabold tracking-tight"
                style={{ color: PHILIPS_BLUE }}
                data-testid="footer-wordmark-philips"
              >
                PHILIPS
              </span>
            </div>
            <p className="text-sm text-neutral-500 max-w-md">
              Campanie exclusivă pentru posesorii de legitimație ISIC, BRD-ISIC,
              ITIC sau IYTC. Reducerile se aplică conform termenilor și
              condițiilor.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://www.facebook.com/isic.romania/?locale=ro_RO"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 rounded-full border border-neutral-200 flex items-center justify-center hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
              aria-label="Facebook"
              data-testid="social-facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href="https://www.instagram.com/legitimatia.isic/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 rounded-full border border-neutral-200 flex items-center justify-center hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
              aria-label="Instagram"
              data-testid="social-instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href="https://www.linkedin.com/company/isic-romania-asyst-/?viewAsMember=true"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 rounded-full border border-neutral-200 flex items-center justify-center hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
              aria-label="LinkedIn"
              data-testid="social-linkedin"
            >
              <Linkedin className="w-4 h-4" />
            </a>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 md:px-10 mt-8 pt-6 border-t border-neutral-100 text-xs text-neutral-500 flex flex-col md:flex-row gap-3 justify-between">
          <span>
            © {new Date().getFullYear()} ISIC România · Toate drepturile
            rezervate.
          </span>
          <span>Philips.ro · Magazin oficial Philips România</span>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
